/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useState } from "react";
import { strings } from "../../../../common/Strings/Strings";
import { InvoiceItemRequest } from "../../../../models/invoice/InvoiceItemRequest";
import AddInventoryItems from "./Inventory/AddInventoryItems";
import AddFees from "./Fee/AddFees";
import AddTreatments from "./Treatment/AddTreatments";
import { CountryDetailsResponse } from "../../../../models/management/CountryDetailsResponse";
import NewCustomCost from "./NewCustomCost";
import Modal from "../../../../components/Modal/Modal";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";
import Tabs from "../../../../components/Tabs/Tabs";

interface Props {
  clinic?: ClinicResponse;
  countryDetails?: CountryDetailsResponse;
  addToCart(item: InvoiceItemRequest): void;
}

enum InvoiceTabs {
  Inventory = "Inventory",
  Fee = "Fee",
  Treatment = "Treatment",
}

const AddProducts = ({ clinic, countryDetails, addToCart }: Props): ReactElement => {
  const [tabKey, setTabKey] = useState<InvoiceTabs>(InvoiceTabs.Inventory);
  const [isAddNewModalOpen, setIsAddNewModalOpen] = useState<boolean>(false);

  const content = () => {
    switch (tabKey) {
      case InvoiceTabs.Inventory:
        return (
          <AddInventoryItems
            addCustomCost={() => {
              setIsAddNewModalOpen(true);
            }}
            addToCart={addToCart}
            clinic={clinic}
            countryDetails={countryDetails}
          />
        );
      case InvoiceTabs.Fee:
        return (
          <AddFees
            addCustomCost={() => {
              setIsAddNewModalOpen(true);
            }}
            addToCart={addToCart}
            countryDetails={countryDetails}
          />
        );
      case InvoiceTabs.Treatment:
        return (
          <AddTreatments
            addCustomCost={() => {
              setIsAddNewModalOpen(true);
            }}
            addToCart={addToCart}
            countryDetails={countryDetails}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <div>
        <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white flex p-4">
          {strings.addProducts}
        </h2>
      </div>
      <div className="p-4">
        <Tabs
          tabs={[
            {
              title: strings.inventory,
              tab: InvoiceTabs.Inventory,
            },
            {
              title: strings.fees,
              tab: InvoiceTabs.Fee,
            },
            {
              title: strings.treatments,
              tab: InvoiceTabs.Treatment,
            },
          ]}
          currentTabKey={tabKey}
          setTab={setTabKey}
        />
        <div>{content()}</div>
      </div>
      <Modal
        handleClose={() => {
          setIsAddNewModalOpen(false);
        }}
        show={isAddNewModalOpen}
      >
        <Modal.Header title={strings.addNew} />
        <Modal.Body>
          <NewCustomCost
            countryDetails={countryDetails}
            submit={(form: InvoiceItemRequest) => {
              addToCart(form);
              setIsAddNewModalOpen(false);
            }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddProducts;

AddProducts.defaultProps = {
  clinicId: undefined,
};
